import { ReactNode, useEffect, useState } from 'react';

export function PageHead({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

export function Card({
  title,
  hint,
  actions,
  children,
  tight,
}: {
  title?: string;
  hint?: string;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="card">
      {title ? (
        <header>
          <div className="row">
            <h3>{title}</h3>
            {hint ? <span className="hint">{hint}</span> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      <div className={tight ? 'body tight' : 'body'}>{children}</div>
    </section>
  );
}

export function Badge({
  tone = '',
  children,
}: {
  tone?: '' | 'ok' | 'warn' | 'danger' | 'accent';
  children: ReactNode;
}) {
  return <span className={tone ? `badge ${tone}` : 'badge'}>{children}</span>;
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`notice ${tone}`}>
      {title ? <h4>{title}</h4> : null}
      <p>{children}</p>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label} {hint ? <span className="hint">— {hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** For content that is read rather than filled in — documentation, code. */
  wide?: boolean;
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={wide ? 'modal wide' : 'modal'} onClick={(event) => event.stopPropagation()}>
        <header>{title}</header>
        <div className="body">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={tab.id === active ? 'active' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note ? <div className="note">{note}</div> : null}
    </div>
  );
}

/**
 * Confirmation for deletions that take more with them than the entity itself.
 * `impact` spells out what disappears alongside it; `confirmText` demands that
 * the name be typed out where the blast radius warrants that much friction.
 */
export function ConfirmDelete({
  title,
  entity,
  impact,
  confirmText,
  confirmLabel = 'Delete',
  onClose,
  onConfirm,
}: {
  title: string;
  entity: ReactNode;
  impact: ReactNode[];
  confirmText?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unlocked = !confirmText || typed.trim() === confirmText;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="danger solid" onClick={() => void submit()} disabled={!unlocked || busy}>
            {busy ? 'Deleting...' : confirmLabel}
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <p className="confirm-lead">{entity}</p>

      {impact.length ? (
        <ul className="impact">
          {impact.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : null}

      <Notice tone="warn">This cannot be undone. The audit trail of past calls stays intact.</Notice>

      {confirmText ? (
        <Field label="Type the name to confirm" hint={confirmText}>
          <input
            type="text"
            value={typed}
            autoFocus
            disabled={busy}
            onChange={(event) => setTyped(event.target.value)}
          />
        </Field>
      ) : null}
    </Modal>
  );
}
